import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Project from '../../../components/Project';
import SkeletonLoader from '../../../components/SkeletonLoader';
import { useToast } from '../../../contexts/ToastContext';
import { fetchProjects, fetchProjectSkills } from '../../../api/projectApi';

function RecentProjectsSection() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToast();

  useEffect(() => {
    const fetchProjectsAndSkills = async () => {
      try {
        setLoading(true);

        const projectsResponse = await fetchProjects({ limit: 3, paginate: false });

        if (!projectsResponse.success) {
          addToast('error', projectsResponse.message || 'Failed to fetch projects.');
          setLoading(false);
          return;
        }

        const fetchedProjects = projectsResponse.data;

        const projectsWithSkillsPromises = fetchedProjects.map(async (project) => {
          if (!project.slug) {
            console.warn(`Project with ID ${project.id} is missing a slug. Skipping skills fetch.`);
            return { ...project, technologies: [] };
          }
          const skillsResponse = await fetchProjectSkills(project.slug);
          if (skillsResponse.success) {
            const technologies = Array.isArray(skillsResponse.data) ? skillsResponse.data.map(skill => skill.name) : [];
            return {
              ...project,
              technologies: technologies,
            };
          } else {
            console.warn(`Failed to fetch skills for project ${project.slug}: ${skillsResponse.message}`);
            return { ...project, technologies: [] };
          }
        });

        const projectsWithSkills = await Promise.all(projectsWithSkillsPromises);
        setProjects(projectsWithSkills);

      } catch (e) {
        console.error("Error fetching recent projects or skills:", e);
        addToast('error', 'Failed to load recent projects. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProjectsAndSkills();
  }, [addToast]);

  // If no projects and not loading, return null to hide the section
  if (!loading && projects.length === 0) {
    return null;
  }

  return (
    <section className="py-16 bg-bg-dark text-text-gray border-y border-border-subtle">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl text-text-white font-bold relative after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-20 after:h-1 after:bg-accent-red after:rounded-sm after:bottom-[-15px] text-center mb-8">
          Featured Projects
        </h2>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            <SkeletonLoader type="project-card" count={3} />
          </div>
        ) : (
          <>
            <Project projects={projects} limit={3} />
            <div className="text-center mt-12">
              <Link to="/projects" className="bg-accent-red text-text-white rounded-full px-8 py-4 font-bold transition-all duration-300 hover:bg-red-700 hover:-translate-y-0.5">
                View All Projects
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default RecentProjectsSection;